import { FOLDERS, type Folder } from '@rp/contracts'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { isApiError } from '../../api/client'
import { Button, EmptyState, Pager, Spinner, Tabs } from '../../design'
import { useReadySession } from '../../session/useSession'
import { useQueueSearch } from '../../shell/queueSearch'
import { deriveClassificationSignals } from '../status/derive'
import { useServiceStatusReporter } from '../status/useServiceStatus'
import { QUEUE_COPY } from './copy'
import { FOLDER_LABELS } from './labels'
import { QueueFilters } from './QueueFilters'
import { QueueItemCard } from './QueueItemCard'
import { QUEUE_DEFAULT_STATUS, QUEUE_PAGE_SIZE, useQueueList, type QueueFilter } from './queries'

const NOW_REFRESH_MS = 60_000

const lastPageOf = (meta: { total: number; limit: number }): number => Math.max(1, Math.ceil(meta.total / meta.limit))

const overrunLastPage = (meta: { total: number; limit: number } | undefined, page: number): number | null => {
  if (meta === undefined || meta.total === 0) return null
  const lastPage = lastPageOf(meta)
  return page > lastPage ? lastPage : null
}

export const QueuePage = ({ currentItemId }: { currentItemId?: string }) => {
  const search = useQueueSearch()
  const navigate = useNavigate({ from: '/' })
  const { me } = useReadySession()
  const report = useServiceStatusReporter()
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  const filter: QueueFilter = {
    folder: search.folder,
    status: QUEUE_DEFAULT_STATUS,
    page: search.page,
    limit: QUEUE_PAGE_SIZE,
    ...(search.intent === undefined ? {} : { intent: search.intent as NonNullable<QueueFilter['intent']> }),
    ...(search.kind === undefined ? {} : { kind: search.kind }),
    ...(search.source === undefined ? {} : { source: search.source }),
    ...(search.author === undefined ? {} : { author: search.author }),
  }

  const query = useQueueList(filter)

  useEffect(() => {
    if (query.data === undefined) return
    for (const signal of deriveClassificationSignals(query.data.items, new Date())) report(signal)
  }, [query.data, report])

  const clampedPage = overrunLastPage(query.data?.meta, filter.page)

  useEffect(() => {
    if (clampedPage === null) return
    void navigate({ search: (previous) => ({ ...previous, page: clampedPage }), replace: true })
  }, [clampedPage, navigate])

  const setFilter = (next: QueueFilter) =>
    void navigate({
      search: {
        folder: next.folder,
        page: next.page,
        ...(next.intent === undefined ? {} : { intent: next.intent }),
        ...(next.kind === undefined ? {} : { kind: next.kind }),
        ...(next.source === undefined ? {} : { source: next.source }),
        ...(next.author === undefined ? {} : { author: next.author }),
      },
    })

  if (query.isPending || clampedPage !== null) return <Spinner label={QUEUE_COPY.loading} />

  if (query.isError) {
    return (
      <EmptyState
        title={QUEUE_COPY.loadFailed}
        description={isApiError(query.error) ? query.error.message : QUEUE_COPY.loadFailed}
        action={
          <Button variant="primary" onClick={() => void query.refetch()}>
            {QUEUE_COPY.retry}
          </Button>
        }
      />
    )
  }

  const { items, meta } = query.data
  const lastPage = lastPageOf(meta)

  return (
    <section className="queue">
      <div className="queue-head">
        <h1 className="queue-title">{QUEUE_COPY.title}</h1>
        <Tabs
          label={QUEUE_COPY.folders}
          value={filter.folder}
          onChange={(folder: Folder) => setFilter({ folder, status: filter.status, page: 1, limit: filter.limit })}
          items={FOLDERS.map((folder) => ({ value: folder, label: FOLDER_LABELS[folder] }))}
        />
      </div>
      <QueueFilters filter={filter} onChange={setFilter} />
      <div id={`tabpanel-${filter.folder}`} role="tabpanel" aria-labelledby={`tab-${filter.folder}`} className="queue-panel">
        {items.length === 0 ? (
          <EmptyState title={QUEUE_COPY.empty[filter.folder]} description={QUEUE_COPY.emptyBody} />
        ) : (
          <ul className="ledger-list queue-list" aria-label={QUEUE_COPY.itemList}>
            {items.map((item) => (
              <QueueItemCard
                key={item.id}
                item={item}
                now={now}
                currentUserId={me.user.id}
                current={item.id === currentItemId}
                onOpen={(id) => void navigate({ to: '/items/$itemId', params: { itemId: id }, search })}
              />
            ))}
          </ul>
        )}
      </div>
      <Pager
        label={QUEUE_COPY.pagination}
        status={QUEUE_COPY.pageStatus(meta.page, meta.total, meta.limit)}
        previousLabel={QUEUE_COPY.previousPage}
        nextLabel={QUEUE_COPY.nextPage}
        previousDisabled={filter.page <= 1}
        nextDisabled={filter.page >= lastPage}
        onPrevious={() => setFilter({ ...filter, page: filter.page - 1 })}
        onNext={() => setFilter({ ...filter, page: filter.page + 1 })}
      />
    </section>
  )
}
